export type TAutoScaleRole = 'pub' | 'pug' | 'both';

export interface IAutoScaleRegion {
  id: string;
  city: string;
  displayName: string;
  /** IANA zone, used to render windows in the region's own local time */
  tz: string;
  plan: string;
}

export interface IAutoScaleSchedule {
  _id: string;
  regionId: string;
  role: TAutoScaleRole;
  /** 0 = Sunday .. 6 = Saturday, in UTC */
  daysOfWeek: number[];
  /** Minutes past midnight UTC, 0-1439 */
  startMinuteUtc: number;
  /** Minutes past midnight UTC, 1-1440. Exclusive - 1440 is midnight. */
  endMinuteUtc: number;
  enabled: boolean;
  note: string;
  /** Computed server side so the UI always agrees with the auto scaler */
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface IAutoScaleLiveServer {
  region: string;
  role: string;
  serverName: string;
  ip: string;
  status: string;
  players: number;
  emptySince: string | null;
  emptyMinutes: number | null;
}

export interface IAutoScaleStatus {
  enabled: boolean;
  plan: string;
  maxInstances: number;
  idleMinutes: number;
  pugIdleMinutes: number;
  regions: { city: string; id: string; plan: string }[];
  roles: string[];
  live: IAutoScaleLiveServer[];
  /** "<regionId>:<role>" entries currently held open by a schedule */
  scheduledSlots: string[];
}

export interface IAutoScalePlan {
  id: string;
  type: string;
  vcpu: number;
  /** MB */
  ram: number;
  /** GB */
  disk: number;
  monthlyCost: number;
  hourlyCost: number;
  /** Vultr region ids this plan can be deployed to */
  locations: string[];
}

/** A region+role slot and the plan it currently resolves to */
export interface IAutoScaleResolvedPlan {
  regionId: string;
  role: string;
  plan: string;
  /** False means it is inheriting the configured default */
  isOverride: boolean;
}

export interface IAutoScaleRegionSetting {
  _id: string;
  regionId: string;
  role: TAutoScaleRole;
  plan: string;
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "voc-g-1c-4gb-30s-amd - 1 vCPU, 4GB, 30GB - $0.041/hr" */
export const describePlan = (plan: IAutoScalePlan): string => {
  const gb = Math.round(plan.ram / 1024);
  return `${plan.id} - ${plan.vcpu} vCPU, ${gb}GB, ${plan.disk}GB - $${plan.hourlyCost}/hr`;
};

/** 930 -> "15:30" */
export const minutesToLabel = (minutes: number): string => {
  const m = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/** "15:30" -> 930 */
export const labelToMinutes = (label: string): number => {
  const [h, m] = label.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/**
 * Offset in minutes between UTC and the zone at the current instant, so windows
 * can be shown in the region's local time. Positive means ahead of UTC.
 *
 * Uses Intl rather than a date library because the offset has to follow DST for
 * the zone, not the browser's own zone.
 */
export const utcOffsetMinutes = (timeZone: string, when: Date = new Date()): number => {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = dtf.formatToParts(when).reduce<Record<string, string>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    // Interpreting the zone's wall clock as if it were UTC gives the offset
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour === '24' ? '00' : parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    return Math.round((asUtc - when.getTime()) / 60000);
  } catch (e) {
    return 0;
  }
};

/** Converts a UTC minute-of-day into the region's local minute-of-day */
export const utcMinuteToLocal = (minute: number, timeZone: string): number => {
  const offset = utcOffsetMinutes(timeZone);
  return (((minute + offset) % 1440) + 1440) % 1440;
};

/** Converts a region-local minute-of-day back to UTC */
export const localMinuteToUtc = (minute: number, timeZone: string): number => {
  const offset = utcOffsetMinutes(timeZone);
  return (((minute - offset) % 1440) + 1440) % 1440;
};

/** "Mon, Tue, Wed" or "Every day" / "Weekdays" / "Weekends" */
export const describeDays = (days: number[]): string => {
  const sorted = [...days].sort((a, b) => a - b);
  const key = sorted.join(',');
  if (key === '0,1,2,3,4,5,6') return 'Every day';
  if (key === '1,2,3,4,5') return 'Weekdays';
  if (key === '0,6') return 'Weekends';
  return sorted.map(d => DAY_LABELS[d]).join(', ');
};
